import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryBilling } from './api';
import { BILLING_INTERNAL_API_KEY } from '../constants/env';

export interface BillingFeature {
  feature_code: string;
  title: string;
  limit: number | string;
  period: string;
  unit?: string | null;
  remaining?: number;
  total_limit?: number;
  expires_at?: string;
  access?: boolean;
}

export interface BillingPackage {
  code: string;
  name: string;
  description1: string;
  description2: string;
  type: string;
  validity_days: number | null;
  price_amount: string;
  can_pay_with_bonus: boolean;
  bonus_payment_limit: number;
  is_popular: boolean;
  base_price: string | null;
  base_duration: string | null;
  base_discount: string | null;
  is_signup_promo: boolean;
  max_activations: number;
  user_activations_count: number;
  metered_features: BillingFeature[];
  features: BillingFeature[];
  display_order: number;
}

export interface BillingShowcaseResponse {
  showcase?: {
    id: string;
    code: string;
    name: string;
    order?: number | null;
  };
  showcases?: Array<{
    showcase?: {
      id: string;
      code: string;
      name: string;
      order?: number | null;
    };
    id?: string;
    code?: string;
    name?: string;
    order?: number | null;
    currency?: string | null;
    packages?: BillingPackage[];
  }>;
  currency: string | null;
  packages?: BillingPackage[];
}

export interface BillingSubscriptionPackage {
  id: string;
  code: string;
  name: string;
  type: string;
  price_amount: string;
  price_currency: string;
  billing_period: number;
  validity_days: number | null;
}

export interface BillingSubscriptionOverview {
  subscription: {
    id: string;
    status: number;
    start_at: string;
    end_at: string;
    package: BillingSubscriptionPackage;
  };
  features: BillingFeature[];
  metered_features: BillingFeature[];
  addons: Array<{
    package: {
      id: string;
      code: string;
      name: string;
    };
    expires_at: string;
    features: BillingFeature[];
  }>;
}

export interface BillingPayment {
  payment_id: number;
  public_order_id: string;
  payment_type: string;
  amount: number;
  currency: string;
  source: string;
  provider: string;
  created_at: string;
  package: {
    id?: string;
    code?: string;
    name?: string;
    type?: string;
    start_at?: string;
    end_at?: string;
  } | null;
  card?: {
    first_six?: string;
    last_four?: string;
    type?: string;
    exp_date?: string;
    issuer?: string;
    issuer_bank_country?: string;
  } | null;
}

export interface BillingSubscriptionItem {
  id: string;
  account_id: number;
  start_at: string;
  end_at: string;
  status: number;
  package: {
    id: string;
    code: string;
    name: string;
    type: string;
    validity_days: number | null;
    priority: number;
  };
}

export interface BillingCombinedOrderResponse {
  public_order_id: string;
  payable_amount: number;
  currency: string;
  status: string;
  created_at: string;
  start_at?: string | null;
  target_subscription_id?: string | null;
}

export interface BillingTranscriptionCombinedOrderRequest {
  quote_token: string;
  consultation_id: string;
  bonus_amount: number;
}

export interface BillingFeatureAccessResponse {
  account_id: number;
  feature_code: string;
  access: boolean;
  remaining?: number;
}

export interface BillingUsageEvent {
  date: string;
  feature: {
    id: string | null;
    code: string | null;
    description: string | null;
  };
  quantity: number;
  unit: string;
  metadata: Record<string, unknown> | null;
}

export interface BillingUsageEventsResponse {
  items: BillingUsageEvent[];
  paging: {
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export const billingApi = createApi({
  reducerPath: 'billing',
  baseQuery: baseQueryBilling,
  endpoints: (builder) => ({
    getBillingShowcase: builder.query<BillingShowcaseResponse, void>({
      query: () => ({
        url: '/packages/showcases',
        method: 'GET',
      }),
    }),
    getBillingSubscriptionOverview: builder.query<BillingSubscriptionOverview, void>({
      query: () => ({
        url: '/subscriptions/overview',
        method: 'GET',
      }),
    }),
    getBillingPayments: builder.query<BillingPayment[], void>({
      query: () => ({
        url: '/orders/payments',
        method: 'GET',
      }),
    }),
    getFrontendSubscriptions: builder.query<BillingSubscriptionItem[], void>({
      query: () => ({
        url: '/subscriptions',
        method: 'GET',
      }),
    }),
    createCombinedOrder: builder.mutation<
      BillingCombinedOrderResponse,
      {
        package_code: string;
        bonus_amount: number;
        target_subscription_id?: string | null;
        start_at?: string | null;
      }
    >({
      query: (body) => ({
        url: '/orders/combined',
        method: 'POST',
        body,
      }),
    }),
    createTranscriptionCombinedOrder: builder.mutation<
      BillingCombinedOrderResponse,
      BillingTranscriptionCombinedOrderRequest
    >({
      query: (body) => ({
        url: '/orders/transcription/combined',
        method: 'POST',
        body,
      }),
    }),
    getFeatureAccess: builder.query<BillingFeatureAccessResponse, string>({
      query: (featureCode) => ({
        url: '/balances/access',
        method: 'GET',
        params: { featureCode },
      }),
    }),
    getUsageEvents: builder.query<BillingUsageEventsResponse, { limit?: number; offset?: number } | void>({
      query: (params) => ({
        url: '/usage-events',
        method: 'GET',
        // Аргумент необязателен, но params в FetchArgs не принимает void.
        params: params || undefined,
      }),
    }),
    cancelOrder: builder.mutation<void, { publicOrderId: string }>({
      query: ({ publicOrderId }) => ({
        url: `/orders/${publicOrderId}/cancel`,
        method: 'POST',
      }),
    }),
    cancelSubscriptionInternal: builder.mutation<
      { status: string; id: string },
      { subscriptionId: string }
    >({
      query: ({ subscriptionId }) => ({
        url: `/internal/subscriptions/${subscriptionId}/cancel`,
        method: 'POST',
        headers: BILLING_INTERNAL_API_KEY ? { 'X-API-Key': BILLING_INTERNAL_API_KEY } : undefined,
      }),
    }),
    activateScheduledSubscriptionNow: builder.mutation<
      { status: string; id: string },
      { subscriptionId: string }
    >({
      query: ({ subscriptionId }) => ({
        url: `/subscriptions/${subscriptionId}/activate-now`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetBillingShowcaseQuery,
  useGetBillingSubscriptionOverviewQuery,
  useGetBillingPaymentsQuery,
  useGetFrontendSubscriptionsQuery,
  useCreateCombinedOrderMutation,
  useCreateTranscriptionCombinedOrderMutation,
  useGetFeatureAccessQuery,
  useGetUsageEventsQuery,
  useCancelOrderMutation,
  useCancelSubscriptionInternalMutation,
  useActivateScheduledSubscriptionNowMutation,
} = billingApi;
